/**
 * Residence Type Selector Component
 * Reusable radio button group for selecting residence type
 */

import { Info } from 'lucide-react'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import type { ResidenceType } from '@/features/applicant/types/address'

interface ResidenceTypeSelectorProps {
  value: ResidenceType
  onChange: (value: ResidenceType) => void
  name?: string
  className?: string
  isReadOnly?: boolean
  variant?: 'inline' | 'cards'
}

const residenceCopy = APPLICANT_CONTENT.application.sections.currentAddress

const RESIDENCE_OPTIONS: { value: ResidenceType; label: string; tooltip?: string }[] = [
  { value: 'rent', label: residenceCopy.rent },
  { value: 'own', label: residenceCopy.own },
  {
    value: 'family',
    label: residenceCopy.familyOrOther,
    tooltip: residenceCopy.otherResidenceTooltip,
  },
]

function ResidenceOptionLabel({ label, tooltip }: { label: string; tooltip?: string }) {
  if (!tooltip) {
    return <span className="text-sm font-medium text-gray-800">{label}</span>
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800">
      {label}
      <span className="group relative inline-flex">
        <Info className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <span
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-56 -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-normal leading-5 text-gray-600 shadow-lg group-hover:block"
        >
          {tooltip}
        </span>
      </span>
    </span>
  )
}

export function ResidenceTypeSelector({
  value,
  onChange,
  name = 'residence',
  className = '',
  isReadOnly = false,
  variant = 'inline',
}: ResidenceTypeSelectorProps) {
  if (variant === 'cards') {
    return (
      <div className={className}>
        <p className="text-sm font-semibold text-gray-700 mb-3">Type of residence*</p>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {RESIDENCE_OPTIONS.map((option) => {
            const selected = value === option.value
            return (
              <label
                key={option.value}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                  isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                } ${
                  selected
                    ? 'border-[#1597ff] bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  checked={selected}
                  onChange={() => !isReadOnly && option.value && onChange(option.value)}
                  disabled={isReadOnly}
                  className="sr-only"
                />
                <ResidenceOptionLabel label={option.label} tooltip={option.tooltip} />
              </label>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`${className} mb-6`}>
      <p className="text-sm font-semibold text-gray-700 mb-3">Type of residence*</p>
      <div className="flex gap-6">
        {RESIDENCE_OPTIONS.map((option) => (
          <label key={option.value} className="flex items-center cursor-pointer group">
            <input
              type="radio"
              name={name}
              checked={value === option.value}
              onChange={() => !isReadOnly && option.value && onChange(option.value)}
              disabled={isReadOnly}
              className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
            <ResidenceOptionLabel label={option.label} tooltip={option.tooltip} />
          </label>
        ))}
      </div>
    </div>
  )
}

